import { ActionMenu, FilterSelect } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

export interface ExpenseFilterState {
  /**
   * WHOSE COST: a member id, or the literal `joint` for expenses with no split recorded.
   *
   * The only narrowing the page offers, and the only one it ever wanted. See `ExpenseFilters`.
   *
   * IT SCALES EVERY FIGURE ON THE PAGE, not only the rows — the server returns that member's share of
   * each total. `scoped-share.ts` in this package holds the rule; `expenseScopeOf` below is how the
   * interface says which of the three positions it is in.
   */
  member?: string;
  /** `active` by default. The archived view is an explicit choice (FR-011). */
  state?: "active" | "archived";
  /**
   * Category and card, which the SERVER still accepts and the interface no longer offers.
   *
   * They are kept on the type because `GET /api/expenses` implements them, `expenses-list.test.ts`
   * pins them, and FR-E8 and FR-E14 still describe them — removing them from the wire is a bigger
   * decision than removing two dropdowns, and one nobody has asked for. Nothing sets them today.
   */
  category?: string;
  cardId?: string;
}

/**
 * Which of the three positions the page's filter is in, and what to call it on screen.
 *
 * EVERY SECTION NEEDS THIS AND NONE OF THEM SHOULD DERIVE IT. The total, the category strip, the trend
 * and the stat cards each have to say whose figures they are showing, and each was reading `filters.member`
 * for itself — one of them not at all, which is how the page came to print the household's categories
 * beneath one member's rows under a heading that named nobody.
 *
 * THE NAME COMES FROM THE MEMBER LIST, and a scope that resolves to nobody degrades to `all` rather than
 * to a blank. The filter only ever offers current members, so an unresolvable id means the member left
 * between the page loading and this render — at which point "the whole household" is the honest reading of
 * a control that no longer has that option.
 */
export type ExpenseScope = { kind: "all" } | { kind: "joint" } | { kind: "member"; name: string };

export function expenseScopeOf(
  filters: { member?: string } | undefined,
  members: readonly { userId: string; displayName: string }[],
): ExpenseScope {
  const wanted = filters?.member;
  if (wanted === undefined) return { kind: "all" };
  if (wanted === "joint") return { kind: "joint" };

  const found = members.find((member) => member.userId === wanted);
  if (!found) return { kind: "all" };
  return { kind: "member", name: found.displayName };
}

/**
 * Whose costs the page is showing.
 *
 * ==========================================================================================
 * ONE FILTER, AT THE TOP OF THE PAGE
 * ==========================================================================================
 *
 * There were four, in a row inside each section — category, card, whose, and an archived toggle —
 * and the whole arrangement was wrong in the way that only becomes obvious once it is on screen. A
 * household of two people does not ask "what do the subscriptions come to" often enough to pay for a
 * dropdown on the page it opens monthly, and it never asks "show me only the ING card" at all, since
 * the card is already a column. Two controls were carrying a question nobody had.
 *
 * "Whose" is the one that earns its place, because it is the question the page exists to answer in
 * every other form too: the settle-up card, the `byMember` card and the "Whose" column are all the
 * same axis, and this is how a reader gets the LIST to agree with them.
 *
 * SO IT SITS IN THE TITLE ROW, where the dashboard's filters sit, in the same `FilterSelect`. A
 * control that governs a whole page belongs beside the page's name; one inside a section claims to
 * govern only that section, which is what the previous arrangement said and could not deliver.
 *
 * IT GOVERNS THE WHOLE PAGE, and that is a correction rather than the original design.
 *
 * It used to govern the trend and the list only, on the argument that the household total answers "what is
 * this household committed to per month" and a filtered total answers something else while looking
 * identical. The argument was right about the DANGER and wrong about the remedy: a page filtered to Anouk
 * showed her rows beneath the household's total, the household's per-category strip and — worst — a trend
 * line drawn at the household's amounts for the rows she happens to be on, which was neither figure. Four
 * sections, four different subjects, one control.
 *
 * So every figure is scoped now and every section says whose it is, in words. `expenseScopeOf` above is
 * the one place that decides, and `scoped-share.ts` is the one place the server divides.
 *
 * TWO THINGS ARE STILL NEVER SCOPED, and both say so on screen. The settle-up is a transfer between two
 * people, so narrowing it to one of them describes no payment anybody makes. And "Who pays what" is the
 * household total decomposed BY member, so under a member scope it is the headline figure again under a
 * second name — the interface drops the card rather than printing it twice, which is the rule it already
 * followed for a household of one.
 *
 * ==========================================================================================
 * THE ARCHIVED TOGGLE IS NOT HERE, AND IT IS NOT A FILTER
 * ==========================================================================================
 *
 * It lives in the list's own header (`ArchivedToggle`), beside the search box. It does not narrow a
 * set — it swaps which set is shown, and the two never mix, because an archived expense contributes
 * to nothing and a list quietly containing both would show rows that do not add up to the figure
 * above them. It also has no meaning for the trend, which already reconstructs what was live in each
 * month. A page-level position would claim both of those things falsely.
 */
export function ExpenseFilters({
  value,
  members = [],
  onChange,
}: {
  value: ExpenseFilterState;
  /**
   * Current active members, from identity — the same list the split editor is offered.
   *
   * ONLY CURRENT MEMBERS ARE OFFERED, and a departed member's rows stay reachable: they are in the
   * unfiltered list and still name them in the "Whose" column. Offering them here would mean
   * resolving names the member list no longer answers for, out of whichever splits happen to be on
   * the page — a second source for a name, which is the rule 020's FR-024 settled.
   */
  members?: readonly { userId: string; displayName: string }[];
  onChange: (next: ExpenseFilterState) => void;
}): ReactNode {
  const t = useT();

  // A household of one has nobody to tell apart, so the control would offer "Anyone", "Joint" and a
  // single name that mean the same thing. Nothing to narrow is nothing to show.
  if (members.length < 2) return null;

  return (
    <FilterSelect
      id="expense-scope"
      label={t("exp.filters.byWhose")}
      value={value.member ?? ""}
      onChange={(next) => {
        /*
         * "Anyone" REMOVES the key rather than setting it to undefined.
         *
         * `exactOptionalPropertyTypes` is on, so `{ member: undefined }` is not the same type as an
         * absent `member` — and it is not the same thing on the wire either: the query builder would
         * serialise the key. Clearing a filter has to leave no trace of it.
         */
        const { member: _dropped, ...rest } = value;
        onChange(next === "" ? rest : { ...rest, member: next });
      }}
      testId="expense-scope"
    >
      <option value="">{t("exp.filters.anyone")}</option>
      {/*
        `joint` is the rows with NO split recorded, which is the household-shared set and a real
        question — it is what the "Whose" column prints as "Joint". It is deliberately not "an equal
        share of everything": the settle-up reads an absent split that way so a transfer can be
        computed without anyone hand-entering 50/50, and applying that rule here would return every
        unsplit row under each member's name, which on most households is the whole list.
      */}
      <option value="joint">{t("exp.filters.jointOnly")}</option>
      {members.map((member) => (
        <option key={member.userId} value={member.userId}>
          {member.displayName}
        </option>
      ))}
    </FilterSelect>
  );
}

/**
 * The table's own options, behind a ⋯ beside the search — and today there is exactly one of them.
 *
 * ==========================================================================================
 * IT WAS A BUTTON IN THE ROW, AND PROMINENCE SHOULD TRACK FREQUENCY
 * ==========================================================================================
 *
 * "Show archived" sat permanently beside the search as a control of equal weight, and it is used
 * perhaps twice a year against a search box used every visit. That inversion is what was reported —
 * "too present in the UI when you barely use it" — and it is the ordinary answer for a rare
 * list-level view switch: Linear's Display menu, GitHub Projects' view options, Notion's ⋯. The
 * header is two controls now, and the rare one is one click away and invisible until asked for.
 *
 * IT ALSO SCALES, which a button does not. The next rare table option — density, an export, column
 * visibility — lands here rather than becoming a fifth control in a row of four.
 *
 * ==========================================================================================
 * A `menuitemcheckbox`, NOT A `menuitem`, AND NOT A FILTER
 * ==========================================================================================
 *
 * Everything the old button's note argued is still true and is why this is a CHECKABLE item rather
 * than two menu entries or a select. It does not narrow a set — it swaps which set is shown, and the
 * two never mix, because an archived expense contributes to nothing and a list quietly containing
 * both would show rows that do not add up to the figure above them. It has no meaning for the trend,
 * which already reconstructs what was live in each month. `aria-checked` carries the state that
 * `aria-pressed` used to; the tick beside the label is the redundant second signal.
 *
 * THE COMPONENT KEEPS ITS NAME AND ITS PROPS. `ArchivedToggle` is what the route renders into
 * `ExpenseList`'s `controls` slot, and what changed is the shape it takes, not what it is for.
 */
export function ArchivedToggle({
  value,
  onChange,
}: {
  value: ExpenseFilterState;
  onChange: (next: ExpenseFilterState) => void;
}): ReactNode {
  const t = useT();
  const showingArchived = value.state === "archived";

  return (
    <ActionMenu
      label={t("exp.list.tableOptions")}
      items={[
        {
          label: t("exp.filters.showArchived"),
          checked: showingArchived,
          onSelect: () => {
            onChange({ ...value, state: showingArchived ? "active" : "archived" });
          },
        },
      ]}
    />
  );
}
